import { inject } from '@angular/core'
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router'
import { OidcSecurityService } from 'angular-auth-oidc-client'
import { map, take } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { AuthService } from '../services/auth.service'
import { OidcToken } from '@core/auth/models/auth.model'
import { PermissionService } from '@core/auth/services/permission.service'
import { AuthRedirectService } from '@core/auth/services/auth-redirect.service'

/**
 * Guard that checks if the user is authenticated by waiting for the OIDC check to complete.
 * If not, it remembers the requested URL and triggers the login flow.
 * Additionally, it checks if the user has the required base access group.
 */
export const authenticatedGuard: CanActivateFn = (_route, state: RouterStateSnapshot): Observable<boolean> => {
  const oidcSecurityService = inject(OidcSecurityService)
  const authService = inject(AuthService)
  const permissionService = inject(PermissionService)
  const authRedirectService = inject(AuthRedirectService)
  const router = inject(Router)

  return oidcSecurityService.checkAuth().pipe(
    take(1),
    map(({ isAuthenticated, userData }) => {
      // Update the signals in the AuthService with the final state
      authService.isAuthenticated.set(isAuthenticated)
      authService.userData.set(userData as OidcToken)

      if (!isAuthenticated) {
        // If not authenticated, remember where the user wanted to go, then
        // trigger the login flow and block navigation
        authRedirectService.store(state.url)
        authService.login()
        return false
      }

      // If authenticated, check for the required user group via PermissionService
      if (permissionService.hasBaseAccess()) {
        return true
      }

      // If missing the required group, redirect to unauthorized page
      void router.navigate(['/unauthorized'])
      return false
    })
  )
}

/**
 * Guard factory that checks if the authenticated user has a specific role.
 * @param requiredRole The role to check for.
 */
export const permissionGuard = (requiredRole: string): CanActivateFn => {
  return async (_route, state: RouterStateSnapshot): Promise<boolean> => {
    const authService = inject(AuthService)
    const authRedirectService = inject(AuthRedirectService)
    const permissionService = inject(PermissionService)
    const router = inject(Router)

    if (!authService.isAuthenticated()) {
      authRedirectService.store(state.url)
      authService.login()
      return false
    }

    const userGroups = permissionService.userGroups()
    const hasRole = userGroups.some(g => g.startsWith(requiredRole))

    if (hasRole) {
      return true
    } else {
      await router.navigate(['/'])
      return false
    }
  }
}
